import React, { useContext, useEffect, useRef, useState } from "react";
import {
  TextField,
  IconButton,
  MenuItem,
  Divider,
} from "@mui/material";
import AddIcon from "../../Images/Addblue.svg";
import DeleteIcon from "../../Images/Deletered.svg";
import { CommonContext } from "../../../context/CommonContext";
import { AuthContext } from "../../../context/AuthContext";
import { chemoProtocolInitialValues, mapChemoProtocolResponse } from "../../../schema/chemoProtocolInitialValues";
import { apisPath } from "../../../utils/path";
import { authHttp } from "../../../services/commonHttpService";
import { Formik } from "formik";
import { chemoProtocolSchema } from "../../../schema/chemoProtocolSchema";

const ChemotherapyProtocol = ({ submitForm, formResult, patientId, currentStep }) => {
  const { setIsLoading, setSnackState } = useContext(CommonContext);
  const { userId } = useContext(AuthContext);

  const formRef = useRef(null);
  const [initialValues, setInitialValues] = useState(chemoProtocolInitialValues);
  const [isFocus, setIsFocus] = useState(false);

  // helper to prepare payload
  const refactorDataBeforeSubmit = (values) => {
    // Ensure chemo_dosage items have keys expected by backend
    const chemo_dosage = Array.isArray(values?.chemo_dosage)
      ? values?.chemo_dosage.map((c) => ({
        chemo_administered_date: c.chemo_administered_date || null,
        chemo_dosage: c.chemo_dosage || "",
      }))
      : [];

    return {
      patient_id: Number(patientId),
      user_id: Number(userId),
      chemo_regimen_details: values?.chemo_regimen_details,
      treatment_intent: values?.treatment_intent,
      vascular_access: values?.vascular_access,
      known_drug_allergies: values?.known_drug_allergies,
      significant_adverse_reactions_reported:
        values?.significant_adverse_reactions_reported,
      chemo_dosage,
      version: 1,
      completed_step: 4,
    };
  };

  // Fetch GET when this step becomes active (index 3)
  useEffect(() => {
    if (currentStep === 3 && patientId) {
      fetchStepData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, patientId]);

  const fetchStepData = async () => {
    if (!patientId) return;
    setIsLoading(true);
    try {
      const res = await authHttp.get(
        `${apisPath?.getPatientTreatmentStep}/${patientId}/4`
      );

      if (res?.data?.data) {
        setInitialValues(mapChemoProtocolResponse(res.data.data));
      }
    } catch (err) {
      // 404 => no data yet (silent); otherwise show alert
      if (err?.response?.status !== 404) {
        setSnackState({
          open: true,
          message: err?.response?.data?.message || "Something went wrong",
          severity: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Parent triggers submit -> Formik submit
  useEffect(() => {
    if (submitForm) {
      formRef?.current?.handleSubmit();

      // small delay to let validation populate errors
      setTimeout(() => {
        if (Object.keys(formRef?.current?.errors || {}).length !== 0) {
          setIsFocus(true);
        }
      }, 100);

      formResult(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitForm]);

  // Focus first error + show alert (same pattern as other steps)
  useEffect(() => {
    const errObj = formRef?.current?.errors || {};
    if (isFocus && Object.keys(errObj).length) {
      const keys = Object.keys(errObj);
      const firstField = window.document.getElementsByName(keys[0])[0];
      if (firstField) firstField.focus();

      setSnackState({
        open: true,
        message: Object.values(errObj)[0],
        severity: "error",
      });

      setIsFocus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocus, formRef?.current?.errors]);

  // Submit handler
  const submitHandler = async (values) => {
    setIsLoading(true);
    try {
      const payload = refactorDataBeforeSubmit(values);
      const res = await authHttp.post(`${apisPath?.patientTreatmentStep}/4`, payload);

      if (res?.data?.success || res?.status === 200) {
        setSnackState({
          open: true,
          message: "Chemotherapy protocol saved successfully",
          severity: "success",
        });

        formResult(true);
      } else {
        setSnackState({
          open: true,
          message: res?.data?.message || "Saved",
          severity: "success",
        });
        formResult(true);
      }
    } catch (err) {
      setSnackState({
        open: true,
        message: err?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
      formResult(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4">
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={chemoProtocolSchema}
        onSubmit={submitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue
        }) => (
          <>
            <h6 className="header-sub mb-3">Current Chemotherapy Protocol</h6>

            {/* Regimen + Intent */}
            <div className="row mb-4">
              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Chemotherapy Regimen Details"
                  size="small"
                  placeholder="Enter regimen details"
                  variant="standard"
                  name="chemo_regimen_details"
                  value={values?.chemo_regimen_details}
                  onChange={handleChange}
                // error={
                //   touched?.chemo_regimen_details &&
                //   Boolean(errors?.chemo_regimen_details)
                // }
                // helperText={
                //   touched?.chemo_regimen_details && errors?.chemo_regimen_details
                // }
                />
              </div>

              <div className="col-md-3 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Intent"
                  size="small"
                  variant="standard"
                  name="treatment_intent"
                  value={values?.treatment_intent}
                  onChange={handleChange}
                // error={touched?.treatment_intent && Boolean(errors?.treatment_intent)}
                // helperText={touched?.treatment_intent && errors?.treatment_intent}
                >
                  <MenuItem value="Definitive">Definitive</MenuItem>
                  <MenuItem value="Adjuvant">Adjuvant</MenuItem>
                  <MenuItem value="Palliative">Palliative</MenuItem>
                  <MenuItem value="New Adjuvant">New Adjuvant</MenuItem>
                  <MenuItem value="Concomitant">Concomitant</MenuItem>
                </TextField>
              </div>
            </div>

            <h6 className="header-sub mb-3">Treatment Provided</h6>

            {/* Dynamic chemo_dosage rows */}
            {Array.isArray(values?.chemo_dosage) &&
              values?.chemo_dosage.map((row, idx) => (
                <div className="row mb-4" key={idx}>
                  {/* Date */}
                  <div className="col-md-3 col-12 mb-3">
                    <label htmlFor={`chemo_date_${idx}`} className="form-label">
                      Date
                    </label>
                    <input
                      type="date"
                      className={`form-control custom-date ${touched?.chemo_dosage &&
                        touched?.chemo_dosage[idx] &&
                        errors?.chemo_dosage &&
                        errors?.chemo_dosage[idx] &&
                        errors?.chemo_dosage[idx].chemo_administered_date
                        ? "is-invalid"
                        : ""
                        }`}
                      id={`chemo_date_${idx}`}
                      name={`chemo_dosage[${idx}].chemo_administered_date`}
                      value={row?.chemo_administered_date}
                      onChange={(e) =>
                        setFieldValue(
                          `chemo_dosage[${idx}].chemo_administered_date`,
                          e.target.value
                        )
                      }
                      onBlur={handleBlur}
                      max={new Date().toISOString().split("T")[0]}
                    />
                    {touched?.chemo_dosage &&
                      touched?.chemo_dosage[idx] &&
                      errors?.chemo_dosage &&
                      errors?.chemo_dosage[idx] &&
                      errors?.chemo_dosage[idx]?.chemo_administered_date && (
                        <div className="invalid-feedback d-block">
                          {errors?.chemo_dosage[idx].chemo_administered_date}
                        </div>
                      )}
                  </div>
                  {/* Dose */}
                  <div className="col-md-3 col-12 mt-4">
                    <TextField
                      fullWidth
                      label={`Dose ${idx + 1}`}
                      size="small"
                      placeholder={`Enter Dose ${idx + 1}`}
                      variant="standard"
                      name={`chemo_dosage[${idx}].chemo_dosage`}
                      value={row.chemo_dosage}
                      onChange={(e) =>
                        setFieldValue(`chemo_dosage[${idx}].chemo_dosage`, e.target.value)
                      }
                      onBlur={handleBlur}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        maxLength: 2,
                      }}
                      error={
                        touched?.chemo_dosage &&
                        touched?.chemo_dosage[idx] &&
                        errors?.chemo_dosage &&
                        errors?.chemo_dosage[idx] &&
                        Boolean(errors.chemo_dosage[idx].chemo_dosage)
                      }
                      helperText={
                        touched?.chemo_dosage &&
                        touched?.chemo_dosage[idx] &&
                        errors?.chemo_dosage &&
                        errors?.chemo_dosage[idx] &&
                        errors.chemo_dosage[idx].chemo_dosage
                      }
                    />

                  </div>

                  {/* Actions */}
                  <div className="col-md-2 col-12 mt-4 d-flex align-items-center  gap-2">
                    {values?.chemo_dosage.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => {
                          const newArr = values?.chemo_dosage.filter((_, i) => i !== idx);

                          // ensure at least one entry remains
                          setFieldValue("chemo_dosage", newArr.length ? newArr : [{ chemo_administered_date: null, chemo_dosage: "" }]);
                        }}
                      >
                        <img src={DeleteIcon} alt="Delete" width={40} height={40} />
                      </IconButton>
                    )}

                    {idx === values?.chemo_dosage.length - 1 &&
                      values?.chemo_dosage.length < 10 && (
                        <IconButton
                          color="primary"
                          onClick={() =>
                            setFieldValue("chemo_dosage", [
                              ...values?.chemo_dosage,
                              { chemo_administered_date: null, chemo_dosage: "" },
                            ])
                          }
                        >
                          <img src={AddIcon} alt="Add" width={42} height={42} />
                        </IconButton>
                      )}
                  </div>
                </div>
              ))}

            {/* Known allergies + vascular access */}
            <div className="row mb-4">
              <div className="col-md-8 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Known Drug Allergies"
                  size="small"
                  placeholder="List any known drug allergies"
                  variant="standard"
                  name="known_drug_allergies"
                  value={values?.known_drug_allergies}
                  onChange={handleChange}
                // error={touched?.known_drug_allergies && Boolean(errors?.known_drug_allergies)}
                // helperText={touched?.known_drug_allergies && errors?.known_drug_allergies}
                />

              </div>

              <div className="col-md-4 col-12 mb-3">
                <TextField
                  fullWidth
                  select
                  label="Vascular Access"
                  size="small"
                  variant="standard"
                  name="vascular_access"
                  value={values?.vascular_access}
                  onChange={handleChange}
                // error={touched?.vascular_access && Boolean(errors?.vascular_access)}
                // helperText={touched?.vascular_access && errors?.vascular_access}
                >
                  <MenuItem value="Peripheral IV">Peripheral IV</MenuItem>
                  <MenuItem value="PICC Line">PICC Line</MenuItem>
                  <MenuItem value="Hickman Line">Hickman Line</MenuItem>
                  <MenuItem value="Port-a-Cath">Port-a-Cath</MenuItem>
                </TextField>

              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-12 col-12 mb-3">
                <TextField
                  fullWidth
                  label="Significant Adverse Reactions Reported"
                  size="small"
                  placeholder="Describe any adverse reactions"
                  variant="standard"
                  name="significant_adverse_reactions_reported"
                  value={values?.significant_adverse_reactions_reported}
                  onChange={handleChange}
                // error={
                //   touched?.significant_adverse_reactions_reported &&
                //   Boolean(errors?.significant_adverse_reactions_reported)
                // }
                // helperText={
                //   touched?.significant_adverse_reactions_reported &&
                //   errors?.significant_adverse_reactions_reported
                // }
                />

              </div>
            </div>

            <Divider className="my-4" />
          </>
        )}
      </Formik>
    </div>
  );
};

export default ChemotherapyProtocol;
